import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Upload, Calculator, History, UserCog, ShieldCheck, Settings, LogOut, Menu, ListChecks, Wallet,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Logo, Copyright } from "../brand/Logo";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { isAdmin, ROLE_LABEL } from "../../lib/format";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/leads", label: "Database Leads", icon: Users },
  { to: "/impor", label: "Impor Leads", icon: Upload, admin: true },
  { to: "/kalkulator", label: "Simulasi Mitra Bank", icon: Calculator },
  { to: "/simulasi", label: "Riwayat Simulasi", icon: History },
  { to: "/progress", label: "List Progress Leads", icon: ListChecks },
  { to: "/klaim", label: "Pengajuan Klaim", icon: Wallet },
  { to: "/pengguna", label: "Pengguna", icon: UserCog, admin: true },
  { to: "/audit", label: "Audit Log", icon: ShieldCheck, admin: true },
  { to: "/pengaturan", label: "Pengaturan", icon: Settings },
];

function NavItems({ user, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1" aria-label="Navigasi utama">
      {NAV.filter((n) => !n.admin || isAdmin(user)).map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          data-testid={`nav-${to.slice(1)}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-[#5720B8]/10 text-[#5720B8]" : "text-slate-600 hover:bg-slate-50 hover:text-[#351080]"
            }`
          }
        >
          <Icon className="h-[18px] w-[18px]" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function UserBox({ user, onLogout }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-100 bg-[#F5F6FA] p-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#351080] text-sm font-bold text-white">
        {user.name?.[0]}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-[#17223B]" data-testid="current-user-name">{user.name}</p>
        <p className="truncate text-xs text-[#5720B8]" data-testid="current-user-role">{ROLE_LABEL[user.role]}</p>
      </div>
      <button onClick={onLogout} className="rounded-md p-2 text-slate-500 hover:bg-white hover:text-red-600" aria-label="Keluar" data-testid="logout-button">
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[264px] flex-col border-r border-slate-200/70 bg-white lg:flex">
        <div className="px-6 pb-6 pt-7">
          <Logo size="sm" data-testid="sidebar-logo" />
        </div>
        <div className="flex-1 overflow-y-auto px-4">
          <NavItems user={user} />
        </div>
        <div className="p-4">
          <UserBox user={user} onLogout={doLogout} />
        </div>
      </aside>

      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200/70 bg-white px-4 py-3 lg:hidden">
        <Logo size="xs" data-testid="header-logo-mobile" />
        <button onClick={() => setOpen(true)} className="rounded-md p-2 text-[#351080]" aria-label="Buka menu" data-testid="mobile-menu-button">
          <Menu className="h-6 w-6" />
        </button>
      </header>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="flex w-[280px] flex-col bg-white p-5">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <Logo size="sm" className="mb-6" />
          <div className="flex-1"><NavItems user={user} onNavigate={() => setOpen(false)} /></div>
          <UserBox user={user} onLogout={doLogout} />
        </SheetContent>
      </Sheet>

      <div className="lg:pl-[264px]">
        <main className="mx-auto max-w-[1360px] px-4 py-8 sm:px-6 lg:px-10 lg:py-10">
          <Outlet />
        </main>
        <footer className="mx-auto flex max-w-[1360px] flex-col items-center gap-3 border-t border-slate-200/70 px-4 py-8 sm:px-6 lg:px-10">
          <Logo size="xs" data-testid="footer-logo" />
          <Copyright />
        </footer>
      </div>
    </div>
  );
}
